import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RegistrationService } from '../../registration/registration.service';
import { CommunicationService } from '../../services/communication.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ConcessionStatus, FileData, FormatterData, UserRoles, UserStatus } from '../../constants/app.constants';
import { Companies } from '../../model/companies';
import { DateFormat } from '../../domains/common.domain';
import { DocumentData } from '../../employee-overview/employee-overview.domain';
import { FileService } from '../../services/file.service';
import { LookupOptionsData } from '../../jurisdiction/jurisdiction.domain';
import { AllDropdownKeys } from '../../jurisdiction/jurisdiction.constants';
import { CommonService } from '../../services/common.service';
import { EnvironmentService } from '../../services/environment.service';
import { SelectItem } from 'primeng';
import { MandatoryDocumentsForGuestByCommodity } from '../../interface/config.interface';
import { ValidationsService } from 'src/app/services/validations.service';
import { BasicProfileRouteData } from 'src/app/registration/basic-profile/basic-profile.domain';

class DetailsFromRowData {
  field: string;
}

@Component({
  selector: 'app-guest-profile',
  templateUrl: './guest-profile.component.html',
  styleUrls: ['./guest-profile.component.scss'],
})
export class GuestProfileComponent implements OnInit, OnDestroy {
  allTitles: LookupOptionsData[];
  dateFormat: DateFormat;
  companyDataFG: FormGroup;
  technicianDataFG: FormGroup;
  checkDataFG: FormGroup;
  technicianApplication: any;
  documents: any;
  userStatus: any;
  isLoading: boolean;
  titleKey: string;

  taId: string;
  companyId: string;
  technicianId: string;
  submitted: boolean = false;
  items: DetailsFromRowData[];
  showAuxiliaryOption: boolean;
  showMainBusinessesPartTimeRadioBtn: boolean;
  showBaseDataEmail: boolean;
  showTechnicianDataEmail: boolean;
  showMandatoryDocument: boolean;
  showProofOfQualification: boolean;
  options: SelectItem[];
  mandatoryDocumentsForGuest: Set<string> = new Set<string>();
  concessionStatus = ConcessionStatus;

  mandatoryDocumentsForGuestByCommodity: MandatoryDocumentsForGuestByCommodity[] =
    this.environmentService.mandatoryDocumentsForGuestByCommodity;
  isReadOnly: boolean;
  fileChanged: boolean = false;

  constructor(
    private registrationService: RegistrationService,
    private communicationService: CommunicationService,
    private localStorageService: LocalStorageService,
    private fileService: FileService,
    private commonService: CommonService,
    private ref: ChangeDetectorRef,
    private environmentService: EnvironmentService
  ) {
    this.dateFormat = FormatterData.date;
    this.companyDataFG = this.registrationService.guestCompanyDataFG;
    this.technicianDataFG = this.registrationService.guestTechnicianDataFG;
    this.checkDataFG = this.registrationService.checkDataFG;
    this.userStatus = UserStatus;
    this.titleKey = AllDropdownKeys.Title;
    this.allTitles = this.commonService.getDropdownData(this.titleKey);
  }

  @Input() routeData: BasicProfileRouteData;

  ngOnInit(): void {
    this.isLoading = true;
    this.showAuxiliaryOption = this.environmentService.showAuxiliaryRadioButton;
    this.showMainBusinessesPartTimeRadioBtn = this.environmentService.showMainBusinessesPartTimeRadioBtn;
    this.showBaseDataEmail = this.environmentService.showBaseDataEmail;
    this.showTechnicianDataEmail = this.environmentService.showTechnicianDataEmail;
    this.showMandatoryDocument = this.environmentService.showMandatoryDocument;
    this.showProofOfQualification = this.environmentService.showProofOfQualification;
    this.items = [{ field: 'power' }, { field: 'gas' }, { field: 'water' }];
    if (this.environmentService.showConcessionDistrictHeating) {
      this.items.push({ field: 'heating' });
    }
    this.options = [
      { label: 'select', value: '' },
      { label: 'master', value: 'Meister' },
      { label: 'engineer', value: 'Ingenieur' },
      { label: 'craft', value: 'Meister In anderem Handwerk' },
      { label: 'journeyman', value: 'Altgeselle' },
      { label: 'exemption', value: 'Ausnahmebewilligung' },
    ];
    this.getTechnicianApplication();
    this.updateValidation();
    this.hideWaterCommodityIfNeeded();
    this.checkDataFG.markAsPristine();
    this.companyDataFG.markAsPristine();
    this.technicianDataFG.markAsPristine();
  }

  get isIdCardRequired(): boolean {
    return this.environmentService.technicianRegistration.isIdCardRequired;
  }

  get showOneDayTrainingTrgiBasedOnSelection(): boolean {
    return this.environmentService.hideDocumentsBasedOnGasOrWaterInput ? !!this.checkDataFG.value.gas : true;
  }

  get showOneDayTrainingTrwiBasedOnSelection(): boolean {
    return this.environmentService.hideDocumentsBasedOnGasOrWaterInput && this.environmentService.showOneDayTrainingTrwi
      ? !!this.checkDataFG.value.water
      : false;
  }

  get showOneDayTrainingTrwi2012(): boolean {
    return !this.environmentService.showOneDayTrainingTrwi;
  }

  getTechnicianApplication(): void {
    const technicianId: string = this.localStorageService.getItem('technician_id');
    this.registrationService.getTechnicianApplication(technicianId, 'technician_id').subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response && response.technician_application && response.technician_application.length) {
          let birthday = response.technician_application[0].technician && response.technician_application[0].technician[0]?.birthday;
          if (birthday) {
            birthday = this.commonService.parseDateForUI(birthday);
            response.technician_application[0].technician[0].birthday = birthday;
          }
          this.setFormValues(response);
          this.isReadOnly = [ConcessionStatus.renew_requested, ConcessionStatus.submitted].includes(
            response.technician_application[0].status?.toUpperCase()
          );
          if (this.isReadOnly) {
            this.technicianDataFG.disable();
            this.companyDataFG.disable();
          } else {
            this.technicianDataFG.enable();
            this.companyDataFG.enable();
          }
        } else {
          this.technicianDataFG.reset();
          this.saveTechnicianApplication(technicianId);
        }
      },
      error => {
        this.isLoading = false;
        if (error.status !== 0) {
          this.technicianDataFG.reset();
          this.saveTechnicianApplication(technicianId);
        }
      }
    );
  }

  saveTechnicianApplication(technicianId: string): void {
    const saveData = this.setTechnicianApplicationForm();
    this.registrationService.saveNewTechnicianApplication(saveData, technicianId).subscribe((response: any) => {
      if (response && response.created && response.created.length) {
        this.taId = response.created[0].ta_id;
        this.registrationService.getTechnicianApplication(this.taId, 'ta_id').subscribe((taResponse: any) => {
          this.setFormValues(taResponse);
        });
      }
    });
  }

  updateTechnician(): void {
    const technician = this.technicianDataFG.value;
    if (technician.birthday) {
      const birthday = technician.birthday;
      if (typeof birthday === 'object') {
        technician.birthday = this.commonService.convertDateObject(birthday);
      } else if (birthday.includes('.')) {
        technician.birthday = this.commonService.parseDateForSaving(birthday);
      } else {
        technician.birthday = birthday;
      }
    }
    const checkData = this.checkDataFG.value;
    if (checkData !== null) {
      technician.concession_water = checkData.water ? '1' : '0';
      technician.concession_power = checkData.power ? '1' : '0';
      technician.concession_gas = checkData.gas ? '1' : '0';
      technician.concession_district_heating = checkData.heating ? '1' : '0';
      technician.welding_license = checkData.license === 'withLicense' ? '1' : '0';
    }
    if (!checkData.heating) {
      checkData.license = null;
      technician.welding_license = '0';
      this.checkDataFG.patchValue(checkData);
    } else {
      if (checkData.license === null) {
        checkData.license = 'withoutLicense';
        this.checkDataFG.patchValue(checkData);
      }
    }

    if (this.mandatoryDocumentsForGuestByCommodity) {
      if (checkData.gas) {
        this.mandatoryDocumentsForGuestByCommodity
          .find(docs => docs.commodity === 'gas')
          .documents.forEach(doc => this.mandatoryDocumentsForGuest.add(doc));
      }

      if (checkData.water) {
        this.mandatoryDocumentsForGuestByCommodity
          .find(docs => docs.commodity === 'water')
          .documents.forEach(doc => this.mandatoryDocumentsForGuest.add(doc));
      }
    }

    technician.status = 'registered';

    const updateData = {
      technician: [technician],
    };
    this.registrationService.updateTechnician(updateData, this.technicianId).subscribe(() => {});
  }

  getCompanyDetails(): void {
    const taxId = this.companyDataFG.get('tax_id').value;
    if (taxId !== null && taxId !== '' && taxId !== undefined) {
      this.communicationService.getCompany(taxId).subscribe((companies: Companies) => {
        if (companies.company !== null && companies.company.length > 0) {
          const companyData = companies.company[0];
          this.companyId = companyData.company_id;
          if (companyData.headoffice === 'true') {
            companyData.headsubsidiary = 'hauptbetrieb';
          }
          if (companyData.subsidiary === 'true') {
            companyData.headsubsidiary = 'nebenbetrieb';
          }
          if (this.showAuxiliaryOption && companyData.auxiliary_company === 'true') {
            companyData.headsubsidiary = 'auxiliary_company';
          }
          if (this.showMainBusinessesPartTimeRadioBtn && companyData.main_businesses_part_time === 'true') {
            companyData.headsubsidiary = 'main_businesses_part_time';
          }
          this.companyDataFG.patchValue(companyData);
          this.updateTechnicianApplication(companyData.company_id);
        } else {
          this.companyDataFG.reset();
          this.companyDataFG.get('tax_id').setValue(taxId);
          this.saveNewCompany();
        }
      });
    } else {
      this.companyDataFG.reset();
    }
  }

  saveNewCompany(): void {
    const company = this.companyDataFG.value;
    company.headoffice = company.headsubsidiary && company.headsubsidiary === 'hauptbetrieb' ? 'true' : 'false';
    company.subsidiary = company.headsubsidiary && company.headsubsidiary === 'nebenbetrieb' ? 'true' : 'false';
    if (this.showAuxiliaryOption) {
      company.auxiliary_company = company.headsubsidiary && company.headsubsidiary === 'auxiliary_company' ? 'true' : 'false';
    }
    if (this.showMainBusinessesPartTimeRadioBtn) {
      company.main_businesses_part_time =
        company.headsubsidiary && company.headsubsidiary === 'main_businesses_part_time' ? 'true' : 'false';
    }
    const companyData = {
      company: [company],
    };
    this.registrationService.saveCompany(companyData).subscribe((savedCompany: any) => {
      if (savedCompany && savedCompany.created && savedCompany.created.length > 0) {
        this.companyId = savedCompany.created[0].company_id;
        this.updateTechnicianApplication(this.companyId);
      }
    });
  }

  updateTechnicianApplication(companyId: string): void {
    const updateData = {
      technician_application: [
        {
          company_id: companyId,
        },
      ],
    };
    this.registrationService.updateTechnicianApplication(updateData, this.taId).subscribe(() => {
      this.getTechnicianApplication();
    });
  }

  updateCompany(): void {
    if (this.companyId !== '' && this.companyId !== null && this.companyId !== undefined) {
      const company = this.companyDataFG.value;
      company.headoffice = company.headsubsidiary && company.headsubsidiary === 'hauptbetrieb' ? 'true' : 'false';
      company.subsidiary = company.headsubsidiary && company.headsubsidiary === 'nebenbetrieb' ? 'true' : 'false';
      if (this.showAuxiliaryOption) {
        company.auxiliary_company = company.headsubsidiary && company.headsubsidiary === 'auxiliary_company' ? 'true' : 'false';
      }
      if (this.showMainBusinessesPartTimeRadioBtn) {
        company.main_businesses_part_time =
          company.headsubsidiary && company.headsubsidiary === 'main_businesses_part_time' ? 'true' : 'false';
      }
      company.company_id = this.companyId;
      const updateData = {
        company: [company],
      };
      this.registrationService.updateCompany(updateData, this.companyId).subscribe(() => {});
    } else {
      this.saveNewCompany();
    }
  }

  saveForm(): void {
    if (this.companyDataFG.valid && this.technicianDataFG.valid) {
      this.updateCompany();
      this.updateTechnician();

      const technician = this.technicianDataFG.value;
      const updateData = {
        technician_application: [
          {
            status: this.routeData?.isRenewConcession ? ConcessionStatus.renew_requested : ConcessionStatus.submitted,
            technician_id: this.technicianId,
            technician: [{ role: UserRoles.guestTechnician }],
            qualification_type: technician.qualification_type,
          },
        ],
      };
      this.deleteFiles();
      const checkData = this.checkDataFG.value;
      if (!checkData.heating || checkData.license === null || checkData.license === 'withoutLicense') {
        const document: DocumentData[] = this.fileService.getDimensionsByFilter(this.documents, FileData.WELDINGCERTIFICATECONFIRMATION);
        if (document !== null && document !== undefined && document.length > 0) {
          this.removeFile(document[0].document_id);
        }
      }
      this.registrationService.updateTechnicianApplication(updateData, this.taId).subscribe(() => {
        this.submitted = true;
        this.getTechnicianApplication();
      });
    } else {
      this.validateFormFields(this.companyDataFG);
      this.validateFormFields(this.technicianDataFG);
    }
  }

  validateFormFields(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateFormFields(control);
      }
    });
  }

  fileEvent(): void {
    this.fileChanged = true;
    this.getTechnicianApplication();
  }

  enabledRadioButtons(): boolean {
    const checkData = this.checkDataFG.value;
    return checkData && checkData.heating;
  }

  enabledFileUpload(): boolean {
    const checkData = this.checkDataFG.value;
    return checkData && checkData.license && checkData.license === 'withLicense';
  }

  removeFile(documentId: string): void {
    this.fileService.removeFile(documentId).subscribe(() => {
      const index = this.fileService.checkIndex(this.documents, documentId);
      if (index !== -1) {
        this.documents.splice(index, 1);
      }
      this.ref.detectChanges();
    });
  }

  getLicenseEnabled(): boolean {
    const checkData = this.checkDataFG.value;
    if (checkData && checkData.license && checkData.license === 'withLicense') {
      return this.getButtonEnabled('welding_certificate_confirmation');
    }
    return false;
  }

  guestProfileFormValidity() {
    return (
      !this.companyDataFG.valid ||
      !this.technicianDataFG.valid ||
      this.getLicenseEnabled() ||
      !this.checkDataFG.valid ||
      this.isIdCardFileUploaded('id_card_main_fraud') ||
      !(this.companyDataFG.dirty || this.technicianDataFG.dirty || this.checkDataFG.dirty || this.fileChanged)
    );
  }

  isIdCardFileUploaded(type: string): boolean {
    return this.isIdCardRequired ? this.getButtonEnabled(type) : false;
  }

  getButtonEnabled(type: string): boolean {
    return !this.fileService.checkForAvailableDocuments(this.documents, type);
  }

  ngOnDestroy(): void {
    this.registrationService.technicianDataFG.reset();
    this.registrationService.companyDataFG.reset();
  }

  private setTechnicianApplicationForm(): any {
    const technician = this.technicianDataFG.value;
    return {
      technician_application: [
        {
          technician: [technician],
        },
      ],
    };
  }

  updateValidation() {
    if (this.showBaseDataEmail) {
      this.companyDataFG.controls.email.setValidators([Validators.required, ValidationsService.emailValidator()]);
      this.companyDataFG.controls.email.updateValueAndValidity();
    }

    if (this.showTechnicianDataEmail) {
      this.technicianDataFG.controls.email.setValidators([Validators.required, ValidationsService.emailValidator()]);
      this.technicianDataFG.controls.email.updateValueAndValidity();
    }

    if (this.showTechnicianDataEmail) {
      this.technicianDataFG.controls.qualification_type.setValidators(Validators.required);
      this.technicianDataFG.controls.qualification_type.updateValueAndValidity();
    }
  }

  getFileEnabled(type: string): boolean {
    const technicianData = this.technicianDataFG.value;
    if (!technicianData.qualification_type) {
      return true;
    }
    if (
      type === 'proof_of_qualification' &&
      (technicianData.qualification_type !== 'Ausnahmebewilligung' || this.showProofOfQualification)
    ) {
      return true;
    }
  }

  deleteFiles(): void {
    const technicianData = this.technicianDataFG.value;
    if (technicianData.qualification_type === 'Ausnahmebewilligung' && !this.showProofOfQualification) {
      const document: DocumentData[] = this.fileService.getDimensionsByFilter(this.documents, FileData.PROOFOFQUALIFICATION);
      if (document !== null && document !== undefined && document.length) {
        this.removeFile(document[0].document_id);
      }
    }
  }

  private setFormValues(response: any): void {
    if (response && response.technician_application && response.technician_application.length) {
      this.technicianApplication = response.technician_application[0];
      if (
        this.technicianApplication &&
        this.technicianApplication.status &&
        (this.technicianApplication.status === UserStatus.SUBMITTED || this.technicianApplication.status === UserStatus.GRANTED)
      ) {
        this.submitted = true;
      } else {
        this.taId = this.technicianApplication.ta_id;
        this.companyId = this.technicianApplication.company_id;
        this.technicianId = this.technicianApplication.technician_id;

        this.parseDocumentValues();

        if (this.technicianApplication.technician && this.technicianApplication.technician.length) {
          this.technicianApplication.technician[0].qualification_type = this.technicianApplication?.qualification_type;
          this.technicianDataFG.patchValue(this.technicianApplication.technician[0]);
          const checkData = {
            power: this.technicianApplication.technician[0].concession_power === '1',
            gas: this.technicianApplication.technician[0].concession_gas === '1',
            water: this.technicianApplication.technician[0].concession_water === '1',
            heating: this.technicianApplication.technician[0].concession_district_heating === '1',
            license: this.technicianApplication.technician[0].welding_license === '1' ? 'withLicense' : 'withoutLicense',
          };
          this.checkDataFG.patchValue(checkData);
        } else {
          this.technicianDataFG.reset();
          this.checkDataFG.reset();
        }
        this.validateFormFields(this.technicianDataFG);
        this.validateFormFields(this.checkDataFG);
        if (this.technicianApplication.company && this.technicianApplication.company.length) {
          const companyData = this.technicianApplication.company[0];
          if (companyData.headoffice === 'true') {
            companyData.headsubsidiary = 'hauptbetrieb';
          }
          if (companyData.subsidiary === 'true') {
            companyData.headsubsidiary = 'nebenbetrieb';
          }
          if (this.showAuxiliaryOption && companyData.auxiliary_company === 'true') {
            companyData.headsubsidiary = 'auxiliary_company';
          }
          if (this.showMainBusinessesPartTimeRadioBtn && companyData.main_businesses_part_time === 'true') {
            companyData.headsubsidiary = 'main_businesses_part_time';
          }
          companyData.company_id = this.technicianApplication.technician[0].company_id;
          this.companyDataFG.patchValue(companyData);
        } else {
          this.companyDataFG.reset();
        }
        this.validateFormFields(this.companyDataFG);
      }
    }
  }

  private parseDocumentValues(): void {
    if (this.technicianApplication && this.technicianApplication.document) {
      this.documents = this.technicianApplication.document.map(data => {
        if (data.filesize.length > 3) {
          data.filesize = (data.filesize / 1000).toFixed(2) + ' kb';
        } else {
          data.filesize = data.filesize + ' bytes';
        }
        return data;
      });
    }
  }

  private hideWaterCommodityIfNeeded() {
    if (this.environmentService.technicianRegistrationHideWaterCommodity) {
      this.items = this.items.filter(i => i.field !== 'water');
    }
  }
}
